import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import './CreateOrderModal.css';

const emptyFormData = {
  customer_name: '',
  customer_phone: '',
  customer_po_number: '',
  item_name: '',
  item_color: '',
  item_sku: '',
  item_size: '',
  quantity: 1,
  price: 0,
  payment_status: 'Not Paid',
  salesperson_id: '',
  notes: '',
};

const CreateOrderModal = ({ isOpen, onClose, onSuccess, initialData }) => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [formData, setFormData] = useState({ ...emptyFormData, ...initialData });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [printStatus, setPrintStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [notesCharCount, setNotesCharCount] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Fetch active users for salesperson dropdown, and (re)apply any prefill data each time the modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setFormData({ ...emptyFormData, ...initialData });
      setErrors({});
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/special-orders/users/active');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  // Format phone number as user types (XXX-XXX-XXXX)
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    } else if (formData.customer_name.length > 255) {
      newErrors.customer_name = 'Customer name cannot exceed 255 characters';
    }

    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone number is required';
    } else if (formData.customer_phone.replace(/\D/g, '').length !== 10) {
      newErrors.customer_phone = 'Phone number must be exactly 10 digits';
    }

    if (!formData.customer_po_number.trim()) {
      newErrors.customer_po_number = 'PO number is required';
    } else if (formData.customer_po_number.length > 100) {
      newErrors.customer_po_number = 'PO number cannot exceed 100 characters';
    }

    if (!formData.item_name.trim()) {
      newErrors.item_name = 'Item name is required';
    } else if (formData.item_name.length > 255) {
      newErrors.item_name = 'Item name cannot exceed 255 characters';
    }

    if (!formData.item_sku.trim()) {
      newErrors.item_sku = 'SKU is required';
    } else if (formData.item_sku.length > 100) {
      newErrors.item_sku = 'SKU cannot exceed 100 characters';
    }

    if (!formData.item_size.trim()) {
      newErrors.item_size = 'Size is required';
    } else if (formData.item_size.length > 50) {
      newErrors.item_size = 'Size cannot exceed 50 characters';
    }

    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (!formData.payment_status) {
      newErrors.payment_status = 'Payment status is required';
    }

    if (!formData.salesperson_id) {
      newErrors.salesperson_id = 'Salesperson is required';
    }

    if (formData.notes && formData.notes.length > 1000) {
      newErrors.notes = 'Notes cannot exceed 1000 characters';
    }

    if (documents.length === 0) {
      newErrors.documents = 'Order confirmation document is required';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for phone number formatting
    if (name === 'customer_phone') {
      processedValue = formatPhoneNumber(value);
    }

    // Handle numeric fields
    if (name === 'quantity' || name === 'price') {
      processedValue = parseFloat(value) || '';
    }

    // Handle notes character count
    if (name === 'notes') {
      setNotesCharCount(value.length);
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Handle document upload
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  // Process files for upload (shared by click and drag/drop)
  const processFiles = (files) => {
    if (files.length === 0) return;

    // Add files to documents array with preview info
    const newDocs = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Date.now() + Math.random().toString(36).substr(2, 9)
    }));

    setDocuments(prev => [...prev, ...newDocs]);

    // Clear the documents error when files are added
    if (errors.documents) {
      setErrors(prev => ({ ...prev, documents: '' }));
    }
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  // Remove document from list
  const handleRemoveDocument = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  // Handle item search via Playwright
  const handleItemSearch = async () => {
    if (!selectedBrand || !searchQuery.trim()) {
      setSearchError('Please select a brand and enter a search term');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const response = await api.post('/special-orders/search-product', {
        brand: selectedBrand,
        query: searchQuery.trim()
      });

      if (response.data.success && response.data.products?.length > 0) {
        setSearchResults(response.data.products);
        setShowSearchResults(true);
      } else {
        setSearchError('No products found matching your search');
      }
    } catch (err) {
      console.error('Search failed:', err);
      setSearchError(err.response?.data?.message || 'Failed to search for products');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle product selection from search results - directly populate form
  const handleSelectProduct = (product) => {
    // Directly populate form with product data (no color selection needed)
    populateFormWithProduct(product);
    setShowSearchResults(false);
  };

  // Populate form with product data
  const populateFormWithProduct = (product) => {
    setFormData(prev => ({
      ...prev,
      item_name: product.fullName || product.name || '',
      item_color: product.color || '',
      item_sku: product.sku || '',
      item_size: product.size || '',
      price: product.price || 0
    }));
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();

      // Add form fields
      submitData.append('customer_name', formData.customer_name);
      submitData.append('customer_phone', formData.customer_phone);
      submitData.append('customer_po_number', formData.customer_po_number || '');
      submitData.append('item_name', formData.item_name);
      submitData.append('item_color', formData.item_color || '');
      submitData.append('item_sku', formData.item_sku || '');
      submitData.append('item_size', formData.item_size || '');
      submitData.append('quantity', formData.quantity);
      submitData.append('price', formData.price);
      submitData.append('payment_status', formData.payment_status);
      submitData.append('salesperson_id', formData.salesperson_id || '');
      submitData.append('notes', formData.notes);

      // Add documents
      documents.forEach((doc) => {
        submitData.append('documents', doc.file);
      });

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
      const response = await axios.post(`${baseURL}/special-orders/orders`, submitData, {
        withCredentials: true,
      });
      setSuccess(true);

      // Capture print status from response
      if (response.data?.print?.status) {
        setPrintStatus(response.data.print.status);
      }

      // Reset form
      setFormData({ ...emptyFormData });
      setNotesCharCount(0);
      setErrors({});
      setDocuments([]);

      // Call onSuccess callback (with the created order) and close modal after a short delay
      setTimeout(() => {
        onSuccess?.(response.data);
        onClose();
        setSuccess(false);
        setPrintStatus(null);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`create-order-modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="create-order-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="create-order-modal-header">
          <h2>{initialData ? 'Convert Inquiry to Order' : 'Create New Order'}</h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message">
            <div>✓ Order created successfully!</div>
            {printStatus && (
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.9 }}>
                {printStatus === 'success' && '🖨️ Sent to printer'}
                {printStatus === 'failed' && '⚠️ Print failed - Order saved'}
                {printStatus === 'pending' && '⏳ Print status pending'}
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Modal Body */}
        <div className="create-order-modal-body">
          <form onSubmit={handleSubmit} className="create-order-form">
            {/* Customer Information */}
            <div className="form-section">
              <h4>Customer Information</h4>

              <div className="form-group">
                <label htmlFor="customer_name">
                  Customer Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  maxLength="255"
                  placeholder="Enter customer name"
                  className={errors.customer_name ? 'input-error' : ''}
                />
                {errors.customer_name && (
                  <span className="error-text">{errors.customer_name}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_phone">
                    Customer Phone <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="customer_phone"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    placeholder="123-456-7890"
                    className={errors.customer_phone ? 'input-error' : ''}
                  />
                  {errors.customer_phone && (
                    <span className="error-text">{errors.customer_phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="customer_po_number">
                    PO Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="customer_po_number"
                    name="customer_po_number"
                    value={formData.customer_po_number}
                    onChange={handleChange}
                    maxLength="100"
                    placeholder="Enter PO number"
                    className={errors.customer_po_number ? 'input-error' : ''}
                  />
                  {errors.customer_po_number && (
                    <span className="error-text">{errors.customer_po_number}</span>
                  )}
                </div>
              </div>

              <div className="form-row item-search-row">
                <div className="form-group brand-select-group">
                  <label htmlFor="brand_select">Brand</label>
                  <select
                    id="brand_select"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                  >
                    <option value="">-- Select Brand --</option>
                    <option value="Hoka">Hoka</option>
                  </select>
                </div>
                <div className="form-group search-input-group">
                  <label htmlFor="item_search">Search</label>
                  <input
                    type="text"
                    id="item_search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter SKU (e.g., 1162031-BHRS)"
                    disabled={!selectedBrand}
                  />
                </div>
                <div className="form-group search-btn-group">
                  <button
                    type="button"
                    className="btn-search-item"
                    onClick={handleItemSearch}
                    disabled={!selectedBrand || !searchQuery.trim() || isSearching}
                  >
                    {isSearching ? 'Searching...' : 'Search for Item'}
                  </button>
                </div>
              </div>
              {searchError && (
                <div className="search-error-message">{searchError}</div>
              )}
            </div>

            {/* Item Information */}
            <div className="form-section">
              <h4>Item Information</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="item_name">
                    Item Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="item_name"
                    name="item_name"
                    value={formData.item_name}
                    onChange={handleChange}
                    maxLength="255"
                    placeholder="Enter item name"
                    className={errors.item_name ? 'input-error' : ''}
                  />
                  {errors.item_name && (
                    <span className="error-text">{errors.item_name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="item_color">
                    Item Color
                  </label>
                  <input
                    type="text"
                    id="item_color"
                    name="item_color"
                    value={formData.item_color}
                    onChange={handleChange}
                    maxLength="100"
                    placeholder="Enter item color"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="item_sku">
                    SKU <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="item_sku"
                    name="item_sku"
                    value={formData.item_sku}
                    onChange={handleChange}
                    maxLength="100"
                    placeholder="Enter SKU"
                    className={errors.item_sku ? 'input-error' : ''}
                  />
                  {errors.item_sku && (
                    <span className="error-text">{errors.item_sku}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="item_size">
                    Size <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="item_size"
                    name="item_size"
                    value={formData.item_size}
                    onChange={handleChange}
                    maxLength="50"
                    placeholder="Enter size"
                    className={errors.item_size ? 'input-error' : ''}
                  />
                  {errors.item_size && (
                    <span className="error-text">{errors.item_size}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="form-section">
              <h4>Order Details</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="quantity">
                    Quantity <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                    step="1"
                    placeholder="Enter quantity"
                    className={errors.quantity ? 'input-error' : ''}
                  />
                  {errors.quantity && (
                    <span className="error-text">{errors.quantity}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="price">
                    Price ($) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Enter price"
                    className={errors.price ? 'input-error' : ''}
                  />
                  {errors.price && (
                    <span className="error-text">{errors.price}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="payment_status">
                    Payment Status <span className="required">*</span>
                  </label>
                  <select
                    id="payment_status"
                    name="payment_status"
                    value={formData.payment_status}
                    onChange={handleChange}
                    className={errors.payment_status ? 'input-error' : ''}
                  >
                    <option value="Not Paid">Not Paid</option>
                    <option value="Paid in Full">Paid in Full</option>
                  </select>
                  {errors.payment_status && (
                    <span className="error-text">{errors.payment_status}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="salesperson_id">
                  Salesperson <span className="required">*</span>
                </label>
                <select
                  id="salesperson_id"
                  name="salesperson_id"
                  value={formData.salesperson_id}
                  onChange={handleChange}
                  className={errors.salesperson_id ? 'input-error' : ''}
                >
                  <option value="">-- Select Salesperson --</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name || user.username}
                    </option>
                  ))}
                </select>
                {errors.salesperson_id && (
                  <span className="error-text">{errors.salesperson_id}</span>
                )}
              </div>

              {/* Order Confirmation Upload Section */}
              <div className="form-group">
                <label>Order Confirmation <span className="required">*</span></label>
                <div
                  className={`document-upload-area ${errors.documents ? 'upload-error' : ''} ${isDragging ? 'dragging' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="document-upload"
                    multiple
                    onChange={handleDocumentUpload}
                    className="document-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                  />
                  <label htmlFor="document-upload" className="document-upload-label">
                    <div className="upload-icon">{isDragging ? '📥' : '📁'}</div>
                    <div className="upload-text">
                      <span className="upload-primary">{isDragging ? 'Drop files here' : 'Click to upload Order Confirmation'}</span>
                      <span className="upload-secondary">{isDragging ? '' : 'or drag and drop files here'}</span>
                    </div>
                    <span className="upload-hint">PDF, Word, Excel, or Images (max 10MB each)</span>
                  </label>
                </div>
                {errors.documents && (
                  <span className="error-text">{errors.documents}</span>
                )}

                {/* Document List */}
                {documents.length > 0 && (
                  <div className="document-list">
                    {documents.map((doc) => (
                      <div key={doc.id} className="document-item">
                        <span className="document-icon">{getFileIcon(doc.type)}</span>
                        <div className="document-info">
                          <span className="document-name">{doc.name}</span>
                          <span className="document-size">{formatFileSize(doc.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="document-remove"
                          onClick={() => handleRemoveDocument(doc.id)}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="notes">
                  Notes <span className="char-count">({notesCharCount}/1000)</span>
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  maxLength="1000"
                  placeholder="Any special instructions or notes"
                  rows="3"
                  className={errors.notes ? 'input-error' : ''}
                ></textarea>
                {errors.notes && (
                  <span className="error-text">{errors.notes}</span>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="create-order-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating Order...' : 'Create Order'}
          </button>
        </div>
      </div>

      {/* Search Results Modal */}
      {showSearchResults && (
        <div className="search-results-overlay" onClick={() => setShowSearchResults(false)}>
          <div className="search-results-modal product-results-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-results-header">
              <h3>Search Results</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowSearchResults(false)}
              >
                &times;
              </button>
            </div>
            <div className="search-results-body">
              {searchResults.length === 0 ? (
                <p className="no-results">No products found</p>
              ) : (
                <>
                  <div className="results-count">
                    <strong>{searchResults.length}</strong> product{searchResults.length !== 1 ? 's' : ''} found
                  </div>
                  <div className="product-results-grid">
                    {searchResults.map((product, index) => (
                      <div
                        key={product.sku || index}
                        className="product-result-card"
                        onClick={() => handleSelectProduct(product)}
                      >
                        {product.imageUrl && (
                          <div className="product-image">
                            <img src={product.imageUrl} alt={product.name} />
                          </div>
                        )}
                        <div className="product-card-info">
                          <span className="product-name">{product.name}</span>
                          <span className="product-color">{product.color}</span>
                          <span className="product-sku">{product.sku}</span>
                          <span className="product-price">MSRP: ${product.price}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateOrderModal;
